import { useUpgradeNeededModal } from "@features/modals/use-upgrade-needed-modal";
import { Plan, usePlanBetterOrEq } from "@features/plans";
import { useUser } from "@features/user/use-user";
import { useEffectPlease } from "@features/utils";
import posthog from "posthog-js";
import { ReactNode, useRef } from "react";
import { useNavigate } from "react-router-dom";

interface UpgradeNeededProps {
  children: ReactNode;
  minimumPlan?: Plan;
  minimumCredits?: number; // Optional, used for credit-based plans
  noModal?: boolean;
  redirect?: boolean;
}

export const UpgradeNeeded: React.FC<UpgradeNeededProps> = ({
  children,
  minimumPlan,
  minimumCredits,
  noModal = false,
  redirect,
}) => {
  const { user } = useUser();
  const { openModal } = useUpgradeNeededModal();
  const isPlanSufficient =
    usePlanBetterOrEq()(minimumPlan || "free") &&
    (user.data?.credits || 0) + (user.data?.additional_credits || 0) >=
      (minimumCredits || 0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffectPlease(() => {
    const handleClick = (event: MouseEvent) => {
      if (user?.data?.id && !isPlanSufficient) {
        event.stopPropagation();
        event.preventDefault();

        posthog.capture("click_upgrade_needed", {
          type: "wrapper",
          minimumPlan,
          minimumCredits,
        });

        if (noModal) {
          if (!redirect) {
            window.open("/billing", "_blank");
          } else {
            navigate("/billing");
          }
        } else {
          openModal({
            minimumPlan: minimumPlan,
            minimumCredits: minimumCredits || 0,
          });
        }
      }
    };

    const currentRef = wrapperRef.current;
    if (currentRef && !isPlanSufficient) {
      currentRef.addEventListener("click", handleClick, { capture: true });
    }

    return () => {
      if (currentRef && !isPlanSufficient) {
        currentRef.removeEventListener("click", handleClick, { capture: true });
      }
    };
  }, [isPlanSufficient, minimumPlan, openModal]);

  return (
    <div ref={wrapperRef} style={{ display: "contents" }}>
      {children}
    </div>
  );
};

export default UpgradeNeeded;
